import { useState, useEffect } from 'react';
import { useAuth } from "../../contexts/AuthContext";
import { db } from "../../firebase";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { useNavigate, useParams } from "react-router";
import * as theme from '../../theme';
import Heading from '../../components/Heading';
import TruCard from '../../components/TruCard';
import { Button, Checkbox, FormControlLabel } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import dayjs from 'dayjs';

export default function ViewOnboardingForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { currentUser } = useAuth();
  const [onboardingForm, setOnboardingForm] = useState(null);
  const [practitioner, setPractitioner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (id) {
      fetchOnboardingForm();
    }
  }, [id]);

  async function fetchOnboardingForm() {
    try {
      setLoading(true);
      const practitionerRef = doc(db, 'practitioners', id);
      const practitionerSnap = await getDoc(practitionerRef);
      if (practitionerSnap.exists() && practitionerSnap.data().onboarding?.form) {
        setOnboardingForm(practitionerSnap.data().onboarding.form);
      } else {
        setError('Onboarding form data not found.');
      }
      if (practitionerSnap.exists()) {
        setPractitioner(practitionerSnap.data());
      }
    } catch (err) {
      console.error('Failed to fetch onboarding form:', err.message);
      setError('Failed to load onboarding form: ' + err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleSupplementToggle(index, isAdded) {
    if (!['admin', 'manager'].includes(currentUser.role)) {
      setError('Unauthorized: Only admins or managers can update supplement status.');
      return;
    }

    try {
      const practitionerRef = doc(db, 'practitioners', id);
      const updatedSupplements = [...onboardingForm.supplementCompanies];
      updatedSupplements[index] = { ...updatedSupplements[index], isAdded: !isAdded };
      await updateDoc(practitionerRef, {
        'onboarding.form.supplementCompanies': updatedSupplements,
      });
      setOnboardingForm({
        ...onboardingForm,
        supplementCompanies: updatedSupplements,
      });
    } catch (err) {
      console.error('Failed to update supplement status:', err.message);
      setError('Failed to update supplement status: ' + err.message);
    }
  }

  async function handleTherapyToggle(index, isAdded) {
    if (!['admin', 'manager'].includes(currentUser.role)) {
      setError('Unauthorized: Only admins or managers can update therapy status.');
      return;
    }

    try {
      const practitionerRef = doc(db, 'practitioners', id);
      const updatedTherapies = [...onboardingForm.specificTherapies];
      updatedTherapies[index] = { ...updatedTherapies[index], isAdded: !isAdded };
      await updateDoc(practitionerRef, {
        'onboarding.form.specificTherapies': updatedTherapies,
      });
      setOnboardingForm({
        ...onboardingForm,
        specificTherapies: updatedTherapies,
      });
    } catch (err) {
      console.error('Failed to update therapy status:', err.message);
      setError('Failed to update therapy status: ' + err.message);
    }
  }

  async function handleAdditionalTherapyToggle(index, isAdded) {
    if (!['admin', 'manager'].includes(currentUser.role)) {
      setError('Unauthorized: Only admins or managers can update additional therapy status.');
      return;
    }

    try {
      const practitionerRef = doc(db, 'practitioners', id);
      const updatedAdditionalTherapies = [...onboardingForm.additionalTherapies];
      updatedAdditionalTherapies[index] = { ...updatedAdditionalTherapies[index], isAdded: !isAdded };
      await updateDoc(practitionerRef, {
        'onboarding.form.additionalTherapies': updatedAdditionalTherapies,
      });
      setOnboardingForm({
        ...onboardingForm,
        additionalTherapies: updatedAdditionalTherapies,
      });
    } catch (err) {
      console.error('Failed to update additional therapy status:', err.message);
      setError('Failed to update additional therapy status: ' + err.message);
    }
  }

  function navigateBack() {
    if (currentUser.role === 'practitioner') {
      navigate(`/dashboard`);
    } else {
      navigate(`/practitioners/${id}`);
    }
  }

  if (loading) {
    return (
      <div style={theme.Container.basic}>
        <div style={theme.Heading}>Practitioner Onboarding Form</div>
        <div>Loading...</div>
      </div>
    );
  }

  if (error || !onboardingForm) {
    return (
      <div style={theme.Container.basic}>
        <div style={theme.Heading}>Practitioner Onboarding Form</div>
        <div style={theme.Error}>{error || 'No onboarding form data available.'}</div>
        <Button
          variant="contained"
          color="secondary"
          onClick={() => navigateBack()}
          style={{ marginTop: 20 }}
        >
          Back to Dashboard
        </Button>
      </div>
    );
  }

  // Handle legacy string formats for supplements and conditions
  let supplements = onboardingForm.supplementCompanies || [];
  if (typeof supplements === 'string') {
    supplements = supplements.split(',').map(s => ({
      name: s.trim(),
      isAdded: false,
      note: '',
    })).filter(s => s.name);
  } else {
    supplements = supplements.map(c => ({
      name: c.name || c,
      isAdded: c.isAdded ?? false,
      note: c.note || '',
    }));
  }

  let specificTherapiesList = onboardingForm.specificTherapies || [];
  if (typeof specificTherapiesList === 'string') {
    specificTherapiesList = specificTherapiesList.split(',').map(s => ({
      name: s.trim(),
      isAdded: false,
      note: ''
    })).filter(s => s.name);
  } else {
    specificTherapiesList = specificTherapiesList.map(t => ({
      name: t.name || t,
      isAdded: t.isAdded ?? false,
      note: t.note || ''
    }));
  }

  let additionalTherapiesList = onboardingForm.additionalTherapies || [];
  if (typeof additionalTherapiesList === 'string') {
    additionalTherapiesList = additionalTherapiesList.split(',').map(s => ({
      name: s.trim(),
      isAdded: false,
      note: ''
    })).filter(s => s.name);
  } else {
    additionalTherapiesList = additionalTherapiesList.map(t => ({
      name: t.name || t,
      isAdded: t.isAdded ?? false,
      note: t.note || ''
    }));
  }

  let conditionsList = onboardingForm.conditions || [];
  if (typeof conditionsList === 'string') {
    conditionsList = conditionsList.split(',').map(s => s.trim()).filter(Boolean);
  }

  let scanningRequestsList = onboardingForm.scanningRequests || [];
  if (typeof scanningRequestsList === 'string') {
    scanningRequestsList = scanningRequestsList.split(',').map(s => s.trim()).filter(Boolean);
  }

  let specialRequestsList = onboardingForm.specialRequests || [];
  if (typeof specialRequestsList === 'string') {
    specialRequestsList = specialRequestsList.split(',').map(s => s.trim()).filter(Boolean);
  }

  let questionsList = onboardingForm.questions || [];
  if (typeof questionsList === 'string') {
    questionsList = questionsList.split(',').map(s => s.trim()).filter(Boolean);
  }

  let detoxCohortsList = onboardingForm.detoxCohorts || [];
  if (typeof detoxCohortsList === 'string') {
    detoxCohortsList = detoxCohortsList.split(',').map(s => s.trim()).filter(Boolean);
  }

  // Get comments object, default to empty object if not present
  const comments = onboardingForm.comments || {};

  return (
    <div style={{ ...theme.Container.flexColumnContainer, ...theme.Container.center800 }}>
      <div style={theme.Container.flexColumn}>
        <Heading
          title={`${practitioner.firstName} ${practitioner.lastName}`}
          onClick={() => navigateBack()}
        />
      </div>
      <div style={theme.Container.fullMax}>
        <div style={theme.Container.flexOne}>
          <Heading
            title={'Onboarding Form'}
            subHeading={true}
            icon={currentUser.role !== 'practitioner' ? 'edit' : null}
            iconAction={() => navigate(`/practitioners/${id}/onboarding/edit`)}
          />
          <div style={{ ...theme.Container.flexColumn, flex: 2, gap: 20 }}>
            <TruCard title="General" style={{ width: '100%' }}>
              <div style={theme.TruCard.SubTextLeft}>
                <div style={{ fontWeight: 600, color: theme.Colors.Purple }}>How many clients do you see weekly?</div>
                <div style={{ marginBottom: 20 }}>{onboardingForm.clientCount || 'None provided'}</div>
                <div style={{ fontWeight: 600, color: theme.Colors.Purple }}>How many clients per month do you anticipate completing a scan?</div>
                <div style={{ marginBottom: 20 }}>{onboardingForm.monthCount || 'None provided'}</div>
                {['admin', 'manager'].includes(currentUser.role) && comments.general && (
                  <>
                    <div style={{ fontWeight: 600, color: theme.Colors.Purple }}>TruBalance Comment</div>
                    <div style={{ marginBottom: 20 }}>{comments.general}</div>
                  </>
                )}
              </div>
            </TruCard>
            <TruCard title="Supplement Companies" style={{ width: '100%' }}>
              <div style={theme.TruCard.SubTextLeft}>
                <div style={{ fontWeight: 600, color: theme.Colors.Purple }}>Supplement companies you use:</div>
                <div style={{ marginBottom: 20 }}>
                  {supplements.length > 0 ? (
                    supplements.map((s, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center' }}>
                        {['admin', 'manager'].includes(currentUser.role) && (
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={s.isAdded}
                                onChange={() => handleSupplementToggle(i, s.isAdded)}
                                icon={<CheckCircleIcon style={{ color: theme.Colors.GrayMedium }} />}
                                checkedIcon={<CheckCircleIcon style={{ color: theme.Colors.Purple }} />}
                              />
                            }
                            label=""
                            style={{ marginRight: 8 }}
                          />
                        )}
                        <div>
                          {s.name}{s.note && s.note.trim() ? ` - ${s.note}` : ''}
                        </div>
                      </div>
                    ))
                  ) : (
                    'None provided'
                  )}
                </div>
                {['admin', 'manager'].includes(currentUser.role) && comments.supplements && (
                  <>
                    <div style={{ fontWeight: 600, color: theme.Colors.Purple }}>TruBalance Comment</div>
                    <div style={{ marginBottom: 20 }}>{comments.supplements}</div>
                  </>
                )}
              </div>
            </TruCard>
            <TruCard title="Conditions Treated" style={{ width: '100%' }}>
              <div style={theme.TruCard.SubTextLeft}>
                <div style={{ fontWeight: 600, color: theme.Colors.Purple }}>Types of conditions/health concerns that you work with:</div>
                <div style={{ marginBottom: 20 }}>
                  {conditionsList.length > 0 ? conditionsList.map((c, i) => <div key={i}>{c}</div>) : 'None provided'}
                </div>
                {['admin', 'manager'].includes(currentUser.role) && comments.conditions && (
                  <>
                    <div style={{ fontWeight: 600, color: theme.Colors.Purple }}>TruBalance Comment</div>
                    <div style={{ marginBottom: 20 }}>{comments.conditions}</div>
                  </>
                )}
              </div>
            </TruCard>
            <TruCard title="Group Detox/Cohorts" style={{ width: '100%' }}>
              <div style={theme.TruCard.SubTextLeft}>
                <div style={{ fontWeight: 600, color: theme.Colors.Purple }}>Do you lead group detox programs/cohorts?</div>
                <div style={{ marginBottom: 20 }}>{onboardingForm.leadsGroupDetox ? 'Yes' : 'No'}</div>
                {onboardingForm.leadsGroupDetox && (
                  <>
                    <div style={{ fontWeight: 600, color: theme.Colors.Purple }}>Would you like to include this in the program?</div>
                    <div style={{ marginBottom: 20 }}>{onboardingForm.groupDetox ? 'Yes' : 'No'}</div>
                  </>
                )}
                {onboardingForm.leadsGroupDetox && onboardingForm.groupDetox && (
                  <>
                    <div style={{ fontWeight: 600, color: theme.Colors.Purple }}>What type of detox cohort(s) do you provide?</div>
                    <div style={{ marginBottom: 20 }}>
                      {detoxCohortsList.length > 0 ? detoxCohortsList.map((c, i) => <div key={i}>{c}</div>) : 'None provided'}
                    </div>
                  </>
                )}
                {['admin', 'manager'].includes(currentUser.role) && comments.groupDetox && (
                  <>
                    <div style={{ fontWeight: 600, color: theme.Colors.Purple }}>TruBalance Comment</div>
                    <div style={{ marginBottom: 20 }}>{comments.groupDetox}</div>
                  </>
                )}
              </div>
            </TruCard>
            <TruCard title="Therapies" style={{ width: '100%' }}>
              <div style={theme.TruCard.SubTextLeft}>
                <div style={{ fontWeight: 600, color: theme.Colors.Purple }}>Therapies or treatments to include on scans:</div>
                <div style={{ marginBottom: 20 }}>
                  {specificTherapiesList.length > 0 ? (
                    specificTherapiesList.map((t, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center' }}>
                        {['admin', 'manager'].includes(currentUser.role) && (
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={t.isAdded}
                                onChange={() => handleTherapyToggle(i, t.isAdded)}
                                icon={<CheckCircleIcon style={{ color: theme.Colors.GrayMedium }} />}
                                checkedIcon={<CheckCircleIcon style={{ color: theme.Colors.Purple }} />}
                              />
                            }
                            label=""
                            style={{ marginRight: 8 }}
                          />
                        )}
                        <div>
                          {t.name}{t.note && t.note.trim() ? ` - ${t.note}` : ''}
                        </div>
                      </div>
                    ))
                  ) : (
                    'None selected'
                  )}
                </div>
                <div style={{ fontWeight: 600, color: theme.Colors.Purple }}>Additional therapies or treatments that you wanted included in your scans:</div>
                <div style={{ marginBottom: 20 }}>
                  {additionalTherapiesList.length > 0 ? (
                    additionalTherapiesList.map((t, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center' }}>
                        {['admin', 'manager'].includes(currentUser.role) && (
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={t.isAdded}
                                onChange={() => handleAdditionalTherapyToggle(i, t.isAdded)}
                                icon={<CheckCircleIcon style={{ color: theme.Colors.GrayMedium }} />}
                                checkedIcon={<CheckCircleIcon style={{ color: theme.Colors.Purple }} />}
                              />
                            }
                            label=""
                            style={{ marginRight: 8 }}
                          />
                        )}
                        <div>
                          {t.name}{t.note && t.note.trim() ? ` - ${t.note}` : ''}
                        </div>
                      </div>
                    ))
                  ) : (
                    'None provided'
                  )}
                </div>
                {['admin', 'manager'].includes(currentUser.role) && comments.therapies && (
                  <>
                    <div style={{ fontWeight: 600, color: theme.Colors.Purple }}>TruBalance Comment</div>
                    <div style={{ marginBottom: 20 }}>{comments.therapies}</div>
                  </>
                )}
              </div>
            </TruCard>
            <TruCard title="Special Requests" style={{ width: '100%' }}>
              <div style={theme.TruCard.SubTextLeft}>
                <div style={{ fontWeight: 600, color: theme.Colors.Purple }}>Scanning requests or things you would like tested:</div>
                <div style={{ marginBottom: 20 }}>
                  {scanningRequestsList.length > 0 ? scanningRequestsList.map((r, i) => <div key={i}>{r}</div>) : 'None provided'}
                </div>
                <div style={{ fontWeight: 600, color: theme.Colors.Purple }}>Report requests in regards to the report your clients will receive:</div>
                <div style={{ marginBottom: 20 }}>
                  {specialRequestsList.length > 0 ? specialRequestsList.map((r, i) => <div key={i}>{r}</div>) : 'None provided'}
                </div>
                {['admin', 'manager'].includes(currentUser.role) && comments.specialRequests && (
                  <>
                    <div style={{ fontWeight: 600, color: theme.Colors.Purple }}>TruBalance Comment</div>
                    <div style={{ marginBottom: 20 }}>{comments.specialRequests}</div>
                  </>
                )}
              </div>
            </TruCard>
            <TruCard title="Questions for TruBalance" style={{ width: '100%' }}>
              <div style={theme.TruCard.SubTextLeft}>
                <div style={{ marginBottom: 20 }}>
                  {questionsList.length > 0 ? questionsList.map((q, i) => <div key={i}>{q}</div>) : 'None provided'}
                </div>
                {['admin', 'manager'].includes(currentUser.role) && comments.questions && (
                  <>
                    <div style={{ fontWeight: 600, color: theme.Colors.Purple }}>TruBalance Comment</div>
                    <div style={{ marginBottom: 20 }}>{comments.questions}</div>
                  </>
                )}
              </div>
            </TruCard>
          </div>
        </div>
      </div>
      <div style={{ marginTop: 20, padding: 20, textAlign: 'center', fontSize: `0.85em`, color: theme.Colors.GrayMedium }}>
        <div style={{ marginBottom: 6 }}><span style={{ color: theme.Colors.Purple, fontWeight: 600 }}>Completed:</span> {onboardingForm.dateCompleted ? dayjs(onboardingForm.dateCompleted.toDate()).format('MMMM D, YYYY h:mm A') : 'N/A'}</div>
        <span style={{ color: theme.Colors.Purple, fontWeight: 600 }}>Modified:</span> {onboardingForm.dateModified ? dayjs(onboardingForm.dateModified.toDate()).format('MMMM D, YYYY h:mm A') : 'N/A'}
      </div>
    </div>
  );
}